import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { Pencil, Sparkles } from "lucide-react";
import { useIntl } from "react-intl";

export function ConfirmReviewCard({
  label,
  children,
  onEdit,
  editTestId,
  className = "",
}: {
  label: ReactNode;
  children: ReactNode;
  onEdit?: () => void;
  editTestId?: string;
  className?: string;
}) {
  const intl = useIntl();
  const editLabel = intl.formatMessage({
    id: "brandOnboarding.confirm.editSection",
    defaultMessage: "Edit section",
  });

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
      className={`group rounded-2xl border border-border/70 bg-card/95 p-4 shadow-sm transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-border hover:shadow-md ${className}`}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
          <Sparkles className="h-3 w-3 text-primary/70 motion-safe:animate-pulse" aria-hidden />
          {label}
        </div>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            data-testid={editTestId}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-border/70 text-foreground opacity-0 transition-[opacity,background,color,border-color] hover:border-foreground hover:bg-foreground hover:text-background group-hover:opacity-100 focus-visible:opacity-100"
            aria-label={editLabel}
            title={editLabel}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      {children}
    </motion.section>
  );
}
